import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { endpoint } from '../assets/api/endpoint'; // adjust if needed

class WebSocketService {
  constructor() {
    this.stompClient = null;
    this.subscriptionsMap = new Map();
    this.connecting = false;
  }

  connect(onConnectCallback) {
    if (this.stompClient?.active || this.stompClient?.connected) {
      if (onConnectCallback) onConnectCallback();
      return Promise.resolve();
    }
    if (this.connecting) {
      return Promise.resolve();
    }

    this.connecting = true;

    return new Promise((resolve, reject) => {
      this.stompClient = new Client({
        webSocketFactory: () => new SockJS(`${endpoint.BASE_URL_STAGING}public/ws`),
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        debug: (msg) => console.log('[STOMP]', msg),
        onConnect: () => {
          this.connecting = false;
          if (onConnectCallback) onConnectCallback();
          resolve();
        },
        onStompError: (frame) => {
          this.connecting = false;
          reject(new Error(`STOMP error: ${frame.headers['message']}`));
        },
        onWebSocketError: (event) => {
          this.connecting = false;
          reject(new Error('WebSocket connection failed'));
        },
        onWebSocketClose: (event) => {
          this.connecting = false;
        }
      });

      this.stompClient.activate();
    });
  }

  disconnect() {
    if (this.stompClient) {
      this.stompClient.deactivate().then(() => {
        this.subscriptionsMap.clear();
        this.connecting = false;
      });
    }
  }

  isConnected() {
    return this.stompClient?.connected ?? false;
  }

  getConnectionInfo() {
    return {
      connected: this.stompClient?.connected ?? false,
      connecting: this.connecting,
      active: this.stompClient?.active ?? false
    };
  }

  async subscribe(destination, callback) {
    if (this.subscriptionsMap.has(destination)) {
      return this.subscriptionsMap.get(destination).subscription;
    }

    if (!this.stompClient?.connected) {
      try {
        await this.connect();
      } catch (err) {
        console.error('[WebSocket] Failed to connect before subscribing:', err);
        throw err;
      }
    }

    try {
      const subscription = this.stompClient.subscribe(destination, (message) => {
        try {
          callback(JSON.parse(message.body));
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error);
        }
      });

      this.subscriptionsMap.set(destination, { subscription, callback });
      return subscription;
    } catch (error) {
      throw error;
    }
  }

  unsubscribe(destination) {
    const entry = this.subscriptionsMap.get(destination);
    if (entry) {
      entry.subscription.unsubscribe();
      this.subscriptionsMap.delete(destination);
    }
  }

  unsubscribeMany(destinations) {
    destinations.forEach((d) => this.unsubscribe(d));
  }

  send(destination, body) {
    if (this.stompClient?.connected) {
      this.stompClient.publish({
        destination,
        body: JSON.stringify(body),
      });
    } else {
      console.warn('[WebSocket] Not connected. Cannot send message.');
      throw new Error('WebSocket is not connected. Cannot send message.');
    }
  }

  getState() {
    return {
      connected: this.stompClient?.connected ?? false,
      connecting: this.connecting,
      active: this.stompClient?.active ?? false,
      subscriptionsCount: this.subscriptionsMap.size
    };
  }
}

const WebSocketInstance = new WebSocketService();
export default WebSocketInstance;
